import React from 'react';
import { Loader2, AlertCircle, Clock, CheckCircle } from 'lucide-react';
import { Badge } from '../ui/badge';
import { EnrichmentStatus } from '../../models/property';

interface PropertyEnrichmentStatusIndicatorProps {
  status?: EnrichmentStatus;
}

interface EnrichmentStatusConfig {
  icon: React.ReactNode;
  label: string;
  color: string;
  description: string;
}

const PropertyEnrichmentStatusIndicator: React.FC<PropertyEnrichmentStatusIndicatorProps> = ({ status }) => {
  if (!status || status === 'COMPLETED') {
    return null;
  }

  // Configure status indicators based on enrichment status
  const getEnrichmentStatusConfig = (status: EnrichmentStatus): EnrichmentStatusConfig | null => {
    switch(status) {
      case 'PENDING':
        return {
          icon: <Clock className="h-4 w-4 animate-pulse" />,
          label: 'Pending',
          color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
          description: 'Data enrichment queued'
        };
      case 'PROCESSING':
        return {
          icon: <Loader2 className="h-4 w-4 animate-spin" />,
          label: 'Processing',
          color: 'bg-blue-50 text-fxer-blue border-blue-200',
          description: 'Retrieving property details'
        };
      case 'COMPLETED':
        return {
          icon: <CheckCircle className="h-4 w-4" />,
          label: 'Completed',
          color: 'bg-green-100 text-green-800 border-green-200',
          description: 'Property data enriched successfully'
        };
      case 'NO_DATA_FOUND':
      case 'FAILED':
      case 'API_ERROR':
      case 'UNKNOWN':
        return {
          icon: <AlertCircle className="h-4 w-4" />,
          label: 'Failed',
          color: 'bg-gray-100 text-gray-800 border-gray-200',
          description: 'Unable to retrieve additional data'
        };
      default:
        return null;
    }
  };

  const statusConfig = getEnrichmentStatusConfig(status);
  if (!statusConfig) return null;

  return (
    <div className={`flex items-center ${statusConfig.color} px-3 py-1.5 rounded-md border max-w-fit`}>
      <span className="mr-2">{statusConfig.icon}</span>
      <span className="text-sm font-medium">{statusConfig.description}</span>
    </div>
  );
};

export default PropertyEnrichmentStatusIndicator;